<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Bloco 2, "Para quem". Mostra os tipos de público que já existiam mais o novo
// "Cartões do Kanban", e a contagem REAL vinda da prévia do servidor (quem
// recebe e quem fica de fora, por motivo).
import { computed } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  cardsVsPeopleSentence,
  excludedReasonsSentence,
} from './campaignFormHelpers';

const props = defineProps({
  state: { type: Object, required: true },
  labelOptions: { type: Array, default: () => [] },
  funnelOptions: { type: Array, default: () => [] },
  stageOptions: { type: Array, default: () => [] },
  agentOptions: { type: Array, default: () => [] },
  contactAttributeOptions: { type: Array, default: () => [] },
  contactAttrValueOptions: { type: Array, default: () => [] },
  csvChipText: { type: String, default: '' },
  csvCount: { type: Number, default: 0 },
  kanbanFunnelName: { type: String, default: '' },
  preview: { type: Object, default: null },
  previewLoading: { type: Boolean, default: false },
  previewError: { type: String, default: '' },
  suppressionLoading: { type: Boolean, default: false },
  suppressionError: { type: String, default: '' },
  suppressionResult: { type: Object, default: null },
});

const emit = defineEmits([
  'changeType',
  'csvUpload',
  'csvRemove',
  'csvTemplate',
  'retryPreview',
  'suppressionUpload',
  'retrySuppression',
  'clearKanbanItems',
]);

const TYPES = [
  { value: 'kanban_items', label: 'Cartões do Kanban' },
  { value: 'labels', label: 'Etiqueta do contato' },
  { value: 'conversation_labels', label: 'Etiqueta da conversa' },
  { value: 'funnel', label: 'Funil inteiro' },
  { value: 'kanban', label: 'Funil e etapa' },
  { value: 'assigned_agent', label: 'Atendente' },
  { value: 'conversation_status', label: 'Situação da conversa' },
  { value: 'contact_attribute', label: 'Campo do CRM' },
  { value: 'csv', label: 'Planilha' },
];

const CONVERSATION_STATUS = [
  { value: 'open', label: 'Aberta' },
  { value: 'pending', label: 'Pendente' },
  { value: 'resolved', label: 'Resolvida' },
  { value: 'snoozed', label: 'Adiada' },
];

// "3 ficam de fora: 2 sem telefone e 1 pediu para não receber."
const excludedSentence = computed(() =>
  excludedReasonsSentence(props.preview?.reasons || {})
);

// "12 cartões, 7 pessoas": só no público de cartões e só quando os dois números
// diferem (cartões do mesmo contato são a mesma pessoa).
const cardsSentence = computed(() =>
  cardsVsPeopleSentence(props.preview, props.state.audienceType)
);

const showEmptyAudience = computed(
  () => !!props.preview && props.preview.receiving === 0 && !props.previewLoading
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="type in TYPES"
        :key="type.value"
        type="button"
        class="px-2.5 py-1 text-xs rounded-full border transition-colors"
        :class="
          state.audienceType === type.value
            ? 'border-n-blue-8 bg-n-alpha-black2 text-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-black2'
        "
        :aria-pressed="state.audienceType === type.value"
        @click="emit('changeType', type.value)"
      >
        {{ type.label }}
      </button>
    </div>

    <!-- Cartões marcados no quadro -->
    <div
      v-if="state.audienceType === 'kanban_items'"
      class="flex items-center gap-2 p-2.5 text-sm rounded-xl bg-n-alpha-black2 text-n-slate-12"
    >
      <span class="i-ph-kanban size-4 shrink-0" />
      <span v-if="state.kanbanItemIds.length">
        {{ state.kanbanItemIds.length }} cartões marcados{{
          kanbanFunnelName ? ` no funil ${kanbanFunnelName}` : ''
        }}
      </span>
      <span v-else class="text-n-slate-11">
        Nenhum cartão veio marcado. Volte ao Kanban, marque os cartões e use
        "Criar campanha".
      </span>
      <span class="flex-1" />
      <button
        v-if="state.kanbanItemIds.length"
        type="button"
        class="text-xs text-n-slate-11 hover:underline"
        @click="emit('clearKanbanItems')"
      >
        limpar
      </button>
    </div>

    <TagMultiSelectComboBox
      v-else-if="['labels', 'conversation_labels'].includes(state.audienceType)"
      v-model="state.selectedAudience"
      :options="labelOptions"
      placeholder="Escolha as etiquetas"
      class="[&>div>button]:bg-n-alpha-black2"
    />

    <div v-else-if="state.audienceType === 'funnel'">
      <ComboBox
        v-model="state.selectedFunnel"
        :options="funnelOptions"
        placeholder="Escolha o funil"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div v-else-if="state.audienceType === 'kanban'" class="grid gap-2 sm:grid-cols-2">
      <ComboBox
        v-model="state.selectedFunnel"
        :options="funnelOptions"
        placeholder="Funil"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <ComboBox
        v-model="state.selectedStage"
        :options="stageOptions"
        placeholder="Etapa"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <ComboBox
      v-else-if="state.audienceType === 'assigned_agent'"
      v-model="state.assignedAgentId"
      :options="agentOptions"
      placeholder="Escolha o atendente"
      class="[&>div>button]:bg-n-alpha-black2"
    />

    <ComboBox
      v-else-if="state.audienceType === 'conversation_status'"
      v-model="state.conversationStatus"
      :options="CONVERSATION_STATUS"
      placeholder="Situação da conversa"
      class="[&>div>button]:bg-n-alpha-black2"
    />

    <div
      v-else-if="state.audienceType === 'contact_attribute'"
      class="grid gap-2 sm:grid-cols-2"
    >
      <ComboBox
        v-model="state.contactAttrKey"
        :options="contactAttributeOptions"
        placeholder="Campo do CRM"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <ComboBox
        v-if="contactAttrValueOptions.length"
        v-model="state.contactAttrValue"
        :options="contactAttrValueOptions"
        placeholder="Valor"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <Input v-else v-model="state.contactAttrValue" placeholder="Valor" />
    </div>

    <div v-else-if="state.audienceType === 'csv'" class="flex flex-col gap-2">
      <label
        class="flex items-center gap-2 px-3 py-2 text-sm border border-dashed rounded-xl cursor-pointer border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
      >
        <span class="i-ph-upload-simple size-4" />
        Enviar planilha CSV
        <input type="file" accept=".csv,text/csv" class="hidden" @change="emit('csvUpload', $event)" />
      </label>
      <div v-if="csvCount" class="flex items-center gap-2 text-xs text-n-slate-11">
        <span>{{ csvChipText }}</span>
        <button type="button" class="hover:underline" @click="emit('csvRemove')">
          remover
        </button>
      </div>
      <button
        type="button"
        class="self-start text-xs text-n-blue-11 hover:underline"
        @click="emit('csvTemplate')"
      >
        Baixar modelo de planilha
      </button>
    </div>

    <!-- Contagem real: quem recebe e quem fica de fora, por motivo -->
    <p v-if="previewLoading" class="text-xs text-n-slate-11">
      Conferindo o público...
    </p>
    <div
      v-else-if="previewError"
      class="flex items-center gap-2 p-2.5 text-xs rounded-xl bg-n-alpha-black2 text-n-ruby-11"
      role="alert"
    >
      <span>{{ previewError }}</span>
      <Button label="Tentar de novo" variant="faded" color="slate" size="xs" @click="emit('retryPreview')" />
    </div>
    <div
      v-else-if="showEmptyAudience"
      class="p-2.5 text-xs rounded-xl bg-n-alpha-black2 text-n-amber-11"
    >
      Ninguém recebe com este público.
      <template v-if="cardsSentence"> {{ cardsSentence }}.</template>
      <template v-if="excludedSentence"> Ficam de fora: {{ excludedSentence }}.</template>
    </div>
    <div
      v-else-if="preview"
      class="flex items-start gap-2 p-2.5 text-xs rounded-xl bg-n-alpha-black2 text-n-slate-11"
    >
      <span class="i-ph-users size-4 shrink-0 mt-0.5" />
      <span>
        <b class="text-n-slate-12">{{ preview.receiving }} pessoas recebem.</b>
        <!-- O espaço inicial é obrigatório: sem ele o condense do Vue cola a
             frase no ponto final do <b> ("recebem.3 ficam de fora"). -->
        <template v-if="cardsSentence">
          {{ ' ' }}{{ cardsSentence }}.
        </template>
        <template v-if="preview.excluded">
          {{ ' ' }}{{ preview.excluded }} ficam de fora: {{ excludedSentence }}.
        </template>
        <template v-else> Ninguém fica de fora.</template>
      </span>
    </div>

    <Accordion title="Opcional" :is-open="false">
      <div class="flex flex-col gap-3 pt-2">
        <div class="flex flex-col gap-1">
          <Input
            v-model="state.audienceDescription"
            label="Como descrever este público"
            placeholder="Ex.: quem pediu proposta e não respondeu"
          />
          <p class="text-xs text-n-slate-11">
            Fica guardado com a campanha e aparece ao lado do resultado dela.
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label
            class="flex items-center gap-2 px-3 py-2 text-sm border border-dashed rounded-xl cursor-pointer border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
          >
            <span class="i-ph-prohibit size-4" />
            Lista de descadastro (telefones que nunca recebem)
            <input
              type="file"
              accept=".csv,text/csv"
              class="hidden"
              @change="emit('suppressionUpload', $event)"
            />
          </label>
          <p v-if="suppressionLoading" class="text-xs text-n-slate-11">Enviando...</p>
          <div v-else-if="suppressionError" class="flex items-center gap-2 text-xs text-n-ruby-11" role="alert">
            <span>{{ suppressionError }}</span>
            <Button label="Tentar de novo" variant="faded" color="slate" size="xs" @click="emit('retrySuppression')" />
          </div>
          <p v-else-if="suppressionResult" class="text-xs text-n-teal-11">
            {{ suppressionResult.created || 0 }} números adicionados ao descadastro
            desta conta.
          </p>
        </div>
      </div>
    </Accordion>
  </div>
</template>
